import React, { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import { useBranding } from '../../contexts/BrandingContext';
import { useTranslation } from '../../contexts/LanguageContext';
import { PublicHeader } from '../../features/marketplace/search/components/PublicHeader';
import { auth } from './authStyles';

// Helpers — keep parity with TrustSignals so admin-edited values
// behave identically across the marketing landing and auth shell.
const adminValue = (raw) => {
  if (raw === undefined || raw === null) return null;
  const s = String(raw);
  return s.trim() ? s : null;
};
const adminFlag = (raw, fallback = true) => {
  if (raw === undefined || raw === null) return fallback;
  const v = String(raw).trim().toLowerCase();
  if (!v) return fallback;
  return !['false', '0', 'no', 'off'].includes(v);
};
const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '·';

const AuthLayout = () => {
  const branding = useBranding();
  const { t } = useTranslation();

  // Track page scroll so the public header switches from the
  // brand-coloured "hero" treatment to the white-bg "scrolled" state
  // exactly like it does on the marketing landing — keeps the visual
  // language consistent between `/` and `/login`.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const primary = branding.primary_color || '#1a73e8';
  const accent = branding.accent_color || primary;
  const gradStart = branding.auth_gradient_start || primary;
  const gradEnd = branding.auth_gradient_end || accent;

  // Value props for the brand panel pull from the Trust Signals that
  // admins already edit in /settings/branding (landing_trust1_title…).
  // No new keys required; nothing is silently overwritten.
  const valueProps = [
    adminValue(branding.landing_trust1_title) || t('layout.auth.vp1', 'KYC-verified hosts only'),
    adminValue(branding.landing_trust2_title) || t('layout.auth.vp2', 'RBI-compliant escrow'),
    adminValue(branding.landing_trust4_title) || t('layout.auth.vp3', 'GST + e-way bills automated'),
  ];

  // Customer testimonial — same keys as the public landing.
  const showQuote = adminFlag(branding.landing_show_testimonial, true);
  const quoteText = adminValue(branding.customer_quote_text);
  const quoteAuthor = adminValue(branding.customer_quote_author);
  const quoteCompany = adminValue(branding.customer_quote_company);
  const renderQuote = showQuote && !!quoteText;

  // Stats band — already admin-controlled.
  const stats = [
    {
      value: branding.auth_stat_warehouses || '500+',
      label: branding.auth_stat_warehouses_label || t('page.hero_section.warehouses', 'Warehouses'),
    },
    {
      value: branding.auth_stat_cities || '50+',
      label: branding.auth_stat_cities_label || t('page.hero_section.cities', 'Cities'),
    },
    {
      value: branding.auth_stat_area || '10M+',
      label: branding.auth_stat_area_label || t('page.hero_section.sqft', 'Sq.Ft'),
    },
  ];

  const platformName = branding.platform_name || 'Khulja Sim Sim';
  const tagline = adminValue(branding.platform_tagline);
  const description = adminValue(branding.platform_description);

  return (
    <div style={{ ...auth.shell, flexDirection: 'column' }}>
      {/* ── Public marketing header — mobile/tablet only ──────────
           Below 992px the marketing brand panel is hidden, so on
           those viewports the auth pages used to render naked. We
           pin the same `/` PublicHeader so the brand logo + Login /
           Register CTAs stay reachable from /login + /register on
           phones and iPad-portrait. Hidden on desktop because the
           full-bleed brand panel already covers the same job. */}
      <div className="d-lg-none">
        <PublicHeader scrolled={scrolled} />
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* ── Left brand panel — desktop only ─────────────────────── */}
      <div className="d-none d-lg-flex flex-column" style={auth.brandPanel(gradStart, gradEnd)}>
        <style>{`@keyframes wsOrbFloat{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-14px,18px) scale(1.05)}}@keyframes wsAuthIn{from{transform:translateY(18px)}to{transform:translateY(0)}}@media(prefers-reduced-motion:reduce){[data-ws-anim]{animation:none!important}}`}</style>
        <div style={{ ...auth.brandPanelGrid, animation: 'wsOrbFloat 14s ease-in-out infinite' }} data-ws-anim aria-hidden />

        {/* Logo + brand name are a single Link so clicking either
            takes the visitor back to the public landing — was a plain
            <img>+<span> before, which made the brand mark on /login
            and /register a dead pixel on desktop. */}
        <Link
          to="/"
          aria-label="Go to home"
          style={{ ...auth.brandHeader, textDecoration: 'none', color: 'inherit' }}
        >
          {/* Icon + wordmark pair, inverted to white so they read on
             the purple gradient panel. ``filter: brightness(0)
             invert(1)`` turns any colour image into a solid white
             silhouette while keeping the alpha channel intact —
             cheap, no design assets to maintain in two colours. */}
          <img
            src={branding.logo_url || '/logo-icon.png'}
            alt=""
            style={{
              height: 52, objectFit: 'contain',
              filter: 'brightness(0) invert(1)',
            }}
          />
          <img
            src={branding.logo_wordmark_url || '/logo-wordmark.png'}
            alt={platformName}
            style={{
              height: 30, objectFit: 'contain',
              filter: 'brightness(0) invert(1)',
            }}
          />
        </Link>

        <div style={{ ...auth.brandBody, animation: 'wsAuthIn 640ms cubic-bezier(.2,.8,.2,1) both' }} data-ws-anim>
          <span style={auth.brandEyebrow(accent)}>
            {t('layout.auth.eyebrow', 'India\'s warehouse marketplace')}
          </span>
          <h2 style={auth.brandHeadline}>
            {tagline || t('layout.auth.headline', 'Sign in to manage your warehouses, bookings and operations.')}
          </h2>
          {description && <p style={auth.brandLede}>{description}</p>}

          <div style={auth.brandBullets}>
            {valueProps.filter(Boolean).map((vp) => (
              <div key={vp} style={auth.brandBullet}>
                <span style={auth.brandBulletDot(accent)}><FiCheck size={12} /></span>
                <span style={auth.brandBulletText}>{vp}</span>
              </div>
            ))}
          </div>

          {renderQuote && (
            <div style={auth.brandQuoteCard}>
              <div style={auth.brandQuoteText}>
                <span style={auth.brandQuoteMark(accent)} aria-hidden>“</span>
                {quoteText}
              </div>
              <div style={auth.brandQuoteAuthor}>
                <span style={auth.brandQuoteAvatar(accent)}>
                  {initials(quoteAuthor || quoteCompany || 'Customer')}
                </span>
                <div>
                  {quoteAuthor && <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.88rem' }}>{quoteAuthor}</div>}
                  {quoteCompany && <div style={auth.brandQuoteAuthorMeta}>{quoteCompany}</div>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={auth.brandStatBand}>
          {stats.map((s) => (
            <div key={s.label} style={auth.brandStatItem}>
              <span style={auth.brandStatValue(accent)}>{s.value}</span>
              <span style={auth.brandStatLabel}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right form panel ──────────────────────────────────────
           On mobile the marketing brand panel above is hidden and the
           PublicHeader at the top already carries the brand chrome —
           so the form panel renders the form alone, full-width and
           centred. We do NOT render the legacy `mobileBrand` strip
           any more; that lived inside the form panel as a flex sibling
           and was breaking the layout (it was rendering NEXT TO the
           form, not above it, because the panel's default flex
           direction is row). */}
      <div style={auth.formPanel}>
        <div style={auth.formInner}>
          <Outlet />
        </div>
      </div>
      </div>
    </div>
  );
};

export default AuthLayout;
